import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG } from '../config/constants';
import { logout } from '../store/slices/authSlice';
import { store } from '../store/store';

interface ApiErrorPayload {
    status: string;
    message: string;
    code?: string;
    subErrors?: string[];
}

interface ApiResponse<T> {
    timeStamp: string;
    data: T;
    error: ApiErrorPayload | null;
}

interface RefreshAccessTokenResponse {
    accessToken: string;
}

// Typed error attached to rejected axios errors so callers can branch on
// `error.apiError.code` (e.g. DUPLICATE_APPLICATION, RATE_LIMIT_EXCEEDED,
// DIGILOCKER_VERIFICATION_FAILED) without re-walking response.data.error.
export interface AppError {
    httpStatus: number;
    statusName: string;
    message: string;
    code?: string;
    subErrors?: string[];
    retryAfter?: number;
}

declare module 'axios' {
    interface AxiosError {
        apiError?: AppError;
    }

    interface InternalAxiosRequestConfig {
        _retry?: boolean;
    }
}

class ApiService {
    private client: AxiosInstance;
    private refreshPromise: Promise<string> | null = null;

    constructor() {
        this.client = axios.create({
            baseURL: API_CONFIG.baseURL,
            timeout: API_CONFIG.timeout,
            withCredentials: true,
            headers: API_CONFIG.headers,
        });

        this.setupInterceptors();
    }

    private isAuthEndpoint(url = ''): boolean {
        return /^\/auth\/(login|signup|refresh|logout)/.test(url);
    }

    private isRefreshEndpoint(url = ''): boolean {
        return /^\/auth\/refresh(?:[/?#]|$)/.test(url);
    }

    private redirectToLogin() {
        const path = window.location.pathname;
        if (path !== '/login' && path !== '/register' && path !== '/oauth/callback') {
            window.location.href = '/login';
        }
    }

    private handleSessionExpired() {
        store.dispatch(logout());
        this.redirectToLogin();
    }

    private refreshAccessToken(): Promise<string> {
        if (!this.refreshPromise) {
            this.refreshPromise = this.client
                .post<ApiResponse<RefreshAccessTokenResponse> | RefreshAccessTokenResponse>('/auth/refresh')
                .then(response => {
                    const data = this.normalizeResponse<RefreshAccessTokenResponse>(response.data);

                    if (!data.accessToken) {
                        throw new Error('No access token received');
                    }

                    localStorage.setItem('auth_token', data.accessToken);
                    return data.accessToken;
                })
                .finally(() => {
                    this.refreshPromise = null;
                });
        }

        return this.refreshPromise;
    }

    private setupInterceptors() {
        // Request interceptor for adding auth token
        this.client.interceptors.request.use(
            config => {
                const token = localStorage.getItem('auth_token');
                const requestUrl = config.url ?? '';
                if (token && config.headers && !this.isRefreshEndpoint(requestUrl)) {
                    config.headers.Authorization = `Bearer ${token}`;
                }
                return config;
            },
            error => Promise.reject(error)
        );

        // Response interceptor for error handling
        this.client.interceptors.response.use(
            (response: AxiosResponse) => response,
            async error => {
                const originalRequest = error.config;

                // 1. Extract the specific error message from Backend Wrapper
                // Backend sends: { data: null, error: { status, message, code?, subErrors? } }
                const backendError = error.response?.data?.error as ApiErrorPayload | undefined;
                const backendErrorMsg = backendError?.message;
                const backendSubErrors = backendError?.subErrors;

                // Create a readable error message
                let finalMessage = backendErrorMsg || 'An unexpected error occurred';
                if (backendSubErrors && backendSubErrors.length > 0) {
                    finalMessage += `: ${backendSubErrors.join(', ')}`;
                }

                // Attach this custom message to the error object so UI can display it
                error.message = finalMessage;

                // Typed access path for callers that need code / subErrors / status
                // without re-walking response.data.error.
                error.apiError = {
                    httpStatus: error.response?.status ?? 0,
                    statusName: backendError?.status ?? '',
                    message: backendErrorMsg ?? finalMessage,
                    code: backendError?.code,
                    subErrors: backendSubErrors,
                };

                if (error.response?.status === 429) {
                    const retryAfterHeader = error.response.headers?.['retry-after'];
                    const retryAfter = retryAfterHeader ? parseInt(retryAfterHeader, 10) : undefined;
                    error.apiError = { ...error.apiError, retryAfter };
                    if (!backendErrorMsg) {
                        error.message = retryAfter
                            ? `Too many attempts. Please wait ${retryAfter} seconds and try again.`
                            : 'Too many attempts. Please try again later.';
                    }
                }

                if (error.response?.status === 401) {
                    if (!originalRequest) {
                        return Promise.reject(error);
                    }

                    // A 401 from auth endpoints is handled by the caller, not by session refresh.
                    const requestUrl: string = originalRequest.url ?? '';
                    const isAuthEndpoint = this.isAuthEndpoint(requestUrl);

                    if (!isAuthEndpoint) {
                        if (originalRequest._retry) {
                            this.handleSessionExpired();
                            return Promise.reject(error);
                        }

                        originalRequest._retry = true;

                        try {
                            const accessToken = await this.refreshAccessToken();
                            originalRequest.headers.Authorization = `Bearer ${accessToken}`;
                            return this.client(originalRequest);
                        } catch (refreshError) {
                            this.handleSessionExpired();
                            return Promise.reject(refreshError);
                        }
                    }
                }

                // Handle other errors
                if (error.response?.status === 403) {
                    console.error('Access forbidden');
                }

                if (error.response?.status === 500) {
                    console.error('Server error');
                }
                return Promise.reject(error);
            }
        );
    }

    // Helper to unwrap the Backend's ApiResponse
    private normalizeResponse<T>(response: unknown): T {
        if (
            response &&
            typeof response === 'object' &&
            'timeStamp' in response &&
            'data' in response &&
            'error' in response
        ) {
            return (response as ApiResponse<T>).data;
        }

        return response as T;
    }

    // CRUD Methods
    async get<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.get(url, config);
        return this.normalizeResponse<T>(response.data);
    }

    async post<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.post(url, data, config);
        return this.normalizeResponse<T>(response.data);
    }

    async put<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.put(url, data, config);
        return this.normalizeResponse<T>(response.data);
    }

    async delete<T>(url: string, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.delete(url, config);
        return this.normalizeResponse<T>(response.data);
    }

    async patch<T>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<T> {
        const response = await this.client.patch(url, data, config);
        return this.normalizeResponse<T>(response.data);
    }

    async upload<T>(url: string, file: File, fieldName = 'file'): Promise<T> {
        const formData = new FormData();
        formData.append(fieldName, file);
        const response = await this.client.post(url, formData, {
            headers: { 'Content-Type': 'multipart/form-data' },
        });
        return this.normalizeResponse<T>(response.data);
    }
}

export const apiService = new ApiService();
